import PropTypes from 'prop-types';
import { Waypoint } from 'react-waypoint';
import React, { useState, useCallback, memo } from 'react';
import { withResizeDetector } from 'react-resize-detector';
import { useIsBreakpointDown, useSkillLevels } from '~/modules/common/hooks';
import { ProjectContextProvider } from '~/modules/resourcing/common/contexts';
import { MobileListItemLoading } from '~/modules/common/components/DetailsPage';
import { usePageTitle } from '~/modules/common/title';
import { SortDirection } from '~/types';
import { ResourceRequestActionMessageSnackBar } from '~/modules/common/components';
import useResourceGridWidthHeightExcludePropCheckHack from '~/modules/common/hooks/useResourceGridWidthHeightExcludePropCheckHack';
import { ProjectChartRowsLoading } from '~/modules/resourcing/common/components';
import ResourceActionsDialogContextProvider from './ResourceActionsDialogContextProvider';
import { useProjectResourceRequests } from './hooks';
import DesktopResourcingPlanPage from './LegacyDesktopResourcingPlanPage';
import MobileResourcingPlanPage from './MobileResourcingPlanPage';
import ResourceRequestToolbarContextProvider from './ResourceRequestToolbarContextProvider';
import useProjectResourcingPlanSearch from './hooks/useProjectResourcingPlanSearch';
import { useGetChartDates } from './hooks/useGetResourceChartInformation';

export const ProjectResourcingPlan = memo(
  ({
    editable,
    project,
    width,
    isLoading,
    setSearchCriteria,
    searchCriteria,
    resourceRequestsMinMaxDateAndStatuses,
    resourceAllocationsMinMaxDateAndStatuses,
    chartSettings
  }) => {
    const [sort, setSort] = useState();

    const onSortChange = useCallback(
      field =>
        setSort({
          field,
          direction:
            sort && sort.field === field && sort.direction === SortDirection.Asc
              ? SortDirection.Desc
              : SortDirection.Asc
        }),
      [sort, setSort]
    );

    usePageTitle(
      isLoading
        ? []
        : [
            { messageId: 'routes.projects' },
            { title: project.displayText },
            { messageId: 'projectPageTabs.resourcing' }
          ]
    );

    const filter = useProjectResourcingPlanSearch({ searchCriteria });

    const isMobile = useIsBreakpointDown('sm');

    const { dateRange: chartDisplayDateRange } = useGetChartDates({
      width,
      resourceRequestsMinMaxDateAndStatuses,
      resourceAllocationsMinMaxDateAndStatuses,
      isMobile
    });

    const {
      loadingRows,
      resourceRequests,
      loadingMoreRows,
      loadMoreRows,
      hasMoreRows
    } = useProjectResourceRequests({
      ...filter,
      projectUri: project.id,
      sort,
      chartDisplayDateRange: isMobile ? undefined : chartDisplayDateRange
    });

    const { isSkillLoading, maxSkillLevel } = useSkillLevels();

    if ((loadingRows || isSkillLoading) && isMobile) {
      return <MobileListItemLoading />;
    }

    const isWaypointDisabled = !isMobile;

    return (
      <ProjectContextProvider project={project}>
        <ResourceRequestToolbarContextProvider
          project={project}
          width={width}
          setSearchCriteria={setSearchCriteria}
          searchCriteria={searchCriteria}
          filter={filter}
          sort={sort}
          onSortChange={onSortChange}
          resourceRequestsMinMaxDateAndStatuses={
            resourceRequestsMinMaxDateAndStatuses
          }
          resourceAllocationsMinMaxDateAndStatuses={
            resourceAllocationsMinMaxDateAndStatuses
          }
          chartSettings={chartSettings}
        >
          {isMobile ? (
            <MobileResourcingPlanPage
              isEditSwitchEnabled={editable}
              resourceRequests={resourceRequests}
              setSearchCriteria={setSearchCriteria}
              searchCriteria={searchCriteria}
            />
          ) : (
            <ResourceActionsDialogContextProvider
              project={project}
              resourceRequests={resourceRequests}
              isEditSwitchEnabled={editable}
              maxSkillLevel={maxSkillLevel}
            >
              <DesktopResourcingPlanPage
                project={project}
                isEditSwitchEnabled={editable}
                resourceRequests={resourceRequests}
                filter={filter}
                hasMoreRows={hasMoreRows}
                loadingRows={loadingRows}
                loadMoreRows={loadMoreRows}
                loadingMoreRows={loadingMoreRows}
              />
            </ResourceActionsDialogContextProvider>
          )}
          {!isWaypointDisabled && !loadingRows && hasMoreRows && (
            <Waypoint
              fireOnRapidScroll
              bottomOffset="-50%"
              onEnter={loadMoreRows}
            />
          )}
        </ResourceRequestToolbarContextProvider>
        <ResourceRequestActionMessageSnackBar />
      </ProjectContextProvider>
    );
  },
  useResourceGridWidthHeightExcludePropCheckHack
);

ProjectResourcingPlan.propTypes = {
  project: PropTypes.object.isRequired,
  editable: PropTypes.bool.isRequired,
  isLoading: PropTypes.bool.isRequired,
  width: PropTypes.number,
  setSearchCriteria: PropTypes.func,
  searchCriteria: PropTypes.object,
  resourceRequestsMinMaxDateAndStatuses: PropTypes.object,
  resourceAllocationsMinMaxDateAndStatuses: PropTypes.object,
  chartSettings: PropTypes.object
};

export const SkipRenderBasedOnWidth = ({ width, ...rest }) => {
  const isMobile = useIsBreakpointDown('sm');

  if (!width) {
    if (isMobile) return <MobileListItemLoading />;

    return <ProjectChartRowsLoading />;
  }

  return <ProjectResourcingPlan width={width} {...rest} />;
};

SkipRenderBasedOnWidth.propTypes = {
  width: PropTypes.number
};

export default withResizeDetector(SkipRenderBasedOnWidth);
